import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, User as UserIcon, QrCode } from "lucide-react";
import AffiliationBadges from "@/components/AffiliationBadges";

export default function DigitalICard() {
  const { user } = useAuth();
  const [card, setCard] = useState(null);

  useEffect(() => {
    api.get("/icard").then(({ data }) => setCard(data));
  }, []);

  if (!card) return (
    <div className="min-h-[60vh] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-sm">
      Loading iCard…
    </div>
  );

  const { association } = card;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid="icard-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Member Document</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mb-2">Digital iCard</h1>
      <p className="text-sm text-white/55 mb-10">
        Co-branded. Tamper-proof. Recognized across set, union, and production.
      </p>

      {/* ============ CARD CANVAS ============ */}
      <div
        className={`${card.user.tier === "stage" ? "icard-stage" : "icard-holo"} cs-grain rounded-3xl p-7 sm:p-9 relative`}
        style={{
          background: "linear-gradient(160deg, #0e0e10 0%, #050505 60%, #0a0a0a 100%)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: card.user.tier === "stage"
            ? "0 30px 80px -25px rgba(236,72,153,0.6), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 30px 80px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
        data-testid="icard-canvas"
      >
        {/* shimmer band */}
        <div className="shimmer absolute inset-x-0 top-0 h-px" />

        {/* TOP: Association lead */}
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center font-display font-bold text-base text-white"
            style={{ background: association?.color, boxShadow: `0 0 0 2px ${association?.color}55, 0 12px 30px -8px ${association?.color}88` }}
            data-testid="icard-association-logo"
          >
            {association?.name?.slice(0, 2)}
          </div>
          <div className="flex-1">
            <div className="font-display text-xl sm:text-2xl uppercase tracking-tight" style={{ color: association?.color }}>
              {association?.name}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">
              {association?.full}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
            <ShieldCheck size={12} className="text-[#F5B82E]" />
            <span className="text-[9px] tracking-[0.3em] uppercase text-white/80">Verified</span>
          </div>
        </div>

        <div className="my-8 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        {/* MIDDLE: Identity */}
        <div className="grid grid-cols-[auto_1fr] gap-6 items-center">
          <div className="relative">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white/5 border border-white/15 flex items-center justify-center overflow-hidden">
              <UserIcon size={40} className="text-white/40" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center"
                 style={{ background: "var(--association-theme)" }}>
              <BadgeCheck size={14} className="text-black" />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Name</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-2xl uppercase tracking-tight" data-testid="icard-name">{card.user.name}</div>
              {card.user.is_member ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/15 text-emerald-300 text-[10px] tracking-[0.18em] uppercase font-semibold"
                      data-testid="icard-member-chip">
                  <BadgeCheck size={10}/> Member
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] tracking-[0.18em] uppercase font-semibold"
                      data-testid="icard-non-member-chip">
                  Non-Member
                </span>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Meta label="Role" value={card.user.primary_role.replace("_", " ").toUpperCase()} />
              <Meta label="Card No." value={card.card_no} tid="icard-card-no" />
              <Meta
                label={card.user.is_member ? "Membership #" : "Status"}
                value={card.user.is_member ? card.user.membership_number : "NON-MEMBER"}
                tid="icard-membership"
              />
            </div>
          </div>
        </div>

        <div className="my-8 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        {/* TIER */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Tier</div>
            <div className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--association-theme)", boxShadow: "0 0 18px var(--association-theme)" }} />
              <span className="font-display uppercase tracking-tight">{card.user.tier?.toUpperCase()}</span>
            </div>
          </div>
          <div className="w-28 h-28 rounded-lg border border-white/15 flex items-center justify-center bg-black/40">
            <QrCode size={88} className="text-white/70" />
          </div>
        </div>

        {/* AFFILIATION BADGES — new taxonomy (JAA / TFJAA / CINTAA / …) */}
        {card.affiliation_badges?.length > 0 && (
          <div className="mt-8" data-testid="icard-affiliations">
            <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-2">
              Industry Affiliations
            </div>
            <AffiliationBadges
              items={card.affiliation_badges}
              testidPrefix="icard-aff-badge"
              compact
            />
          </div>
        )}

        {/* BOTTOM: Powered by stamp */}
        <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.35em] uppercase text-white/35">
            Issued · {new Date(card.issued_at).toLocaleDateString()}
          </div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/60" data-testid="icard-powered-by">
            Powered by <span className="text-white font-display font-bold">CALL</span><span className="text-[#C0C0C0] font-display font-bold">SHEET</span>
          </div>
        </div>
      </div>

      <p className="mt-6 text-xs text-white/40 text-center">
        Show this card at set entry, union verification desks, and to authorized vendor partners.
      </p>
      <div className="mt-4 text-center">
        <Link to="/icard/gallery" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#F5B82E] hover:text-white"
              data-testid="goto-gallery">
          View co-branded gallery →
        </Link>
      </div>
    </div>
  );
}

function Meta({ label, value, tid }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">{label}</div>
      <div className="text-sm text-white/90 font-medium mt-0.5" data-testid={tid}>{value}</div>
    </div>
  );
}
